import * as React from "react";
import { cn } from "@/lib/utils";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline" | "ghost" | "destructive" | "secondary" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", asChild, children, ...props }, ref) => {
    const baseClasses = cn(
      "inline-flex items-center justify-center rounded-md font-medium",
      "transition-all duration-300 ease-out",
      "focus-visible:outline-none",
      "disabled:pointer-events-none disabled:opacity-40",
      "cursor-pointer",
      "hover:scale-110 hover:-translate-y-0.5",
      "active:scale-95",
      {
        "bg-foreground text-background hover:opacity-90 hover:shadow-xl":
          variant === "default",
        "border border-border bg-transparent hover:bg-muted hover:shadow-lg hover:border-foreground/20":
          variant === "outline",
        "hover:bg-muted hover:shadow-md": variant === "ghost",
        "bg-red-600 text-white hover:bg-red-700 hover:shadow-xl":
          variant === "destructive",
        "bg-muted text-foreground hover:bg-muted/80": variant === "secondary",
        "text-primary underline-offset-4 hover:underline bg-transparent shadow-none hover:scale-100 hover:translate-y-0":
          variant === "link",
        "h-10 px-6": size === "default",
        "h-9 px-4 text-sm": size === "sm",
        "h-12 px-8 text-base": size === "lg",
        "h-9 w-9 p-0": size === "icon",
      },
      className
    );

    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<any>;
      return React.cloneElement(child, {
        ...props,
        className: cn(baseClasses, child.props?.className),
      });
    }

    return (
      <button
        className={baseClasses}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button };

